import {
	lazy,
	Suspense,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { Link } from "react-router";
import { BuilderToastProvider } from "../components/builder/BuilderToast";
import {
	DeprecatedBarrelBanner,
	TWO_ROOM_PANORAMA_MIGRATION_URL,
} from "../components/builder/DeprecatedBarrelBanner";
import { useRulesContext } from "../context/useRulesContext";
import {
	type BuilderTemplate,
	getTemplatesForType,
} from "../data/builderTemplates";
import { useAuth } from "../hooks/useAuth";
import type { Dictionary } from "../i18n";
import { useT } from "../i18n";
import { configureThreeLogging } from "../lib/configureThreeLogging";
import { trackConversionEvent } from "../lib/conversionTelemetry";
import { validateOpeningSet } from "../domain/openings/validateOpeningSet";
import type { SaunaType } from "../types/design";
import { BuilderIntro } from "./builder/components/BuilderIntro";
import { BuilderMilestoneAnnouncer } from "./builder/components/BuilderMilestoneAnnouncer";
import { BuilderSummaryRail } from "./builder/components/BuilderSummaryRail";
import { BuilderToolbar } from "./builder/components/BuilderToolbar";
import { BuilderWorkspaceNav } from "./builder/components/BuilderWorkspaceNav";
import {
	readIntroSeen,
	shouldShowBuilderIntro,
	suppressViewerHelpAutoOpen,
} from "./builder/components/builderIntroState";
import { FloatingSpecPill } from "./builder/components/FloatingSpecPill";
import { MobileActionBar } from "./builder/components/MobileActionBar";
import { WorkspaceRouter } from "./builder/components/WorkspaceRouter";
import { useBuilderActions } from "./builder/useBuilderActions";
import { useBuilderDerivedData } from "./builder/useBuilderDerivedData";
import { useBuilderDesignLifecycle } from "./builder/useBuilderDesignLifecycle";
import { useBuilderPageState } from "./builder/useBuilderPageState";
import type { BuilderWorkspace } from "./builder/types";
import { useBuilderRouting } from "./builder/useBuilderRouting";
import { useMilestoneFeedback } from "./builder/useMilestoneFeedback";
import { WORKSPACE_PANEL_ID, workspaceTabId } from "./builder/workspaces";
import "../components/builder/builder-toast.css";
import "./BuilderPage.css";

const BuilderRenderResultModal = lazy(() =>
	import("./builder/components/BuilderRenderResultModal").then(
		({ BuilderRenderResultModal }) => ({ default: BuilderRenderResultModal }),
	),
);
const DesignInsightsPanel = lazy(() =>
	import("../components/builder/insights/DesignInsightsPanel").then(
		({ DesignInsightsPanel }) => ({ default: DesignInsightsPanel }),
	),
);
const ShareDialog = lazy(() =>
	import("./builder/components/ShareDialog").then(({ ShareDialog }) => ({
		default: ShareDialog,
	})),
);
const TemplateGalleryModal = lazy(() =>
	import("../components/builder/TemplateGalleryModal").then(
		({ TemplateGalleryModal }) => ({ default: TemplateGalleryModal }),
	),
);

function BuilderSurfaceLoadingFallback() {
	const t = useT();
	return <div role="status">{t.common_loading}</div>;
}

/** Map SaunaType to the localized toolbar label. */
function getEngineTypeLabel(type: SaunaType, t: Dictionary): string {
	const map: Record<SaunaType, string> = {
		fyrkantig: t.toolbar_typeSquare,
		tunna: t.toolbar_typeBarrel,
		tvarum: t.toolbar_typeTwoRoom,
		inomhus: t.toolbar_typeIndoor,
	};
	return map[type] ?? type;
}

export function BuilderPage() {
	useEffect(() => {
		configureThreeLogging();
	}, []);

	const { user } = useAuth();
	const { overrides } = useRulesContext();
	const routing = useBuilderRouting();
	const builderState = useBuilderPageState();
	const t = useT();
	const engineLabel = getEngineTypeLabel(routing.type, t);

	const lifecycle = useBuilderDesignLifecycle({
		designId: routing.designId,
		type: routing.type,
		market: routing.market,
		initialSeedKey: routing.initialSeedKey,
		initialDimensions: routing.initialDimensions,
		initialPartitionSplit: routing.initialPartitionSplit,
		initialTwoRoomConfig: routing.twoRoomConfig,
		initialRoofConfig: routing.initialRoofConfig,
		initialThermalInputs: routing.initialThermalInputs,
		initialSiteConditions: routing.initialSiteConditions,
		initialClimateProfile: routing.initialClimateProfile,
		wizardZip: routing.wizardZip,
		wizardClimate: routing.wizardClimate,
		initialWizardCapacityHint: routing.initialWizardCapacityHint,
		mergeSearchParams: routing.mergeSearchParams,
		navigateToType: routing.navigateToType,
		resetToDefaults: builderState.resetToDefaults,
		userId: user?.id ?? null,
		shareConfig: routing.shareConfig,
	});

	const derived = useBuilderDerivedData({
		engine: routing.engine,
		saunaType: routing.type,
		dimensions: builderState.dimensions,
		partitionSplit: builderState.partitionSplit,
		twoRoomConfig: builderState.twoRoomConfig,
		openings: builderState.openings,
		deckConfig: builderState.deckConfig,
		roofConfig: builderState.roofConfig,
		thermalInputs: builderState.thermalInputs,
		siteConditions: builderState.siteConditions,
		interiorLayout: builderState.interiorLayout,
		finishes: builderState.finishes,
		wallHeightM: builderState.wallHeightM,
		indoorRoom: builderState.indoorRoom,
		rotationDeg: builderState.rotationDeg,
		market: builderState.market,
		locationMode: builderState.locationMode,
		climateProfile: builderState.climateProfile,
		postalCode: builderState.postalCode,
		overrides,
		activeWorkspaceMeta: routing.activeWorkspaceMeta,
		buildProgress: builderState.buildProgress,
	});
	const actions = useBuilderActions({
		type: routing.type,
		projectName: builderState.projectName,
		market: builderState.market,
		locationMode: builderState.locationMode,
		climateProfile: builderState.climateProfile,
		postalCode: builderState.postalCode,
		dimensions: builderState.dimensions,
		partitionSplit: builderState.partitionSplit,
		twoRoomConfig: builderState.twoRoomConfig,
		visibility: builderState.visibility,
		openings: builderState.openings,
		deckConfig: builderState.deckConfig,
		roofConfig: builderState.roofConfig,
		thermalInputs: builderState.thermalInputs,
		buildProgress: builderState.buildProgress,
		siteConditions: builderState.siteConditions,
		interiorLayout: builderState.interiorLayout,
		finishes: builderState.finishes,
		wallHeightM: builderState.wallHeightM,
		indoorRoom: builderState.indoorRoom,
		estimatedTotal: derived.estimatedTotal,
		heatUpLabel: derived.runningCost?.heatUpLabel ?? null,
		rotationDeg: builderState.rotationDeg,
		wizardCapacityHint: builderState.wizardCapacityHint,
		setVisibility: builderState.setVisibility,
		setOpenings: builderState.setOpenings,
		engineLabel,
		calcData: derived.calcData,
		buildInput: derived.buildInput,
		heater: derived.heater,
	});

	const handleWorkspaceAdvance = (workspace: BuilderWorkspace) => {
		if (routing.activeWorkspace === "pricing" && workspace === "build") {
			trackConversionEvent("funnel_build_started", {
				market: builderState.market,
				sauna_type: routing.type,
			});
		}
		routing.setActiveWorkspace(workspace);
	};

	// Bumped whenever the design is replaced wholesale rather than edited, so
	// the milestone latches re-baseline instead of reading the jump as an
	// achievement the user just earned.
	const [milestoneResyncToken, setMilestoneResyncToken] = useState(0);

	// Quiet milestone feedback. Session-only (refs + local state) - nothing is
	// added to the store, and the compliance status is folded out of the same
	// derived.calcData the badges and insights already read.
	const milestones = useMilestoneFeedback({
		calcData: derived.calcData,
		completenessComplete: derived.completeness.missingLabels.length === 0,
		// This hook sits above the loading early return, so until the design has
		// resolved the only thing observable is the blank seed.
		settled: !builderState.loadingDesign,
		resyncToken: milestoneResyncToken,
	});

	useEffect(() => {
		if (builderState.loadingDesign) return;
		if (
			!validateOpeningSet({
				openings: builderState.openings,
				dimensions: builderState.dimensions,
				wallHeightM: builderState.wallHeightM ?? undefined,
			}).valid
		) {
			return;
		}
		trackConversionEvent(
			"funnel_design_valid",
			{ market: builderState.market, sauna_type: routing.type },
			{ once: "first-valid-design" },
		);
	}, [
		builderState.dimensions,
		builderState.loadingDesign,
		builderState.market,
		builderState.openings,
		builderState.wallHeightM,
		routing.type,
	]);

	const availableTemplates = getTemplatesForType(routing.type);
	const [templatesOpen, setTemplatesOpen] = useState(false);
	const [templatesLoaded, setTemplatesLoaded] = useState(false);
	const [insightsOpen, setInsightsOpen] = useState(false);
	// First-run intro gate — decided once on mount from the routing state
	// (no loaded design / share config) and the localStorage seen flag.
	// Share and load flows never see the intro.
	const [introOpen, setIntroOpen] = useState(() => {
		if (routing.hasSeedDimensions) suppressViewerHelpAutoOpen();
		return shouldShowBuilderIntro({
			hasSeedDimensions: routing.hasSeedDimensions,
			hasLoadedDesign: Boolean(routing.designId),
			hasShareConfig: Boolean(routing.shareConfig),
			introSeen: readIntroSeen(),
		});
	});
	const [barrelSaveConfirmOpen, setBarrelSaveConfirmOpen] = useState(false);
	const barrelSaveDialogRef = useRef<HTMLDivElement | null>(null);
	const barrelSaveCancelRef = useRef<HTMLButtonElement | null>(null);
	const barrelSavePreviousFocusRef = useRef<HTMLElement | null>(null);

	const handleOpenTemplates = useCallback(() => {
		setTemplatesLoaded(true);
		setTemplatesOpen(true);
	}, []);

	const handleCloseTemplates = useCallback(() => {
		setTemplatesOpen(false);
	}, []);

	const handleIntroDismiss = useCallback(() => {
		setIntroOpen(false);
	}, []);

	const handleIntroChooseTemplate = useCallback(() => {
		setTemplatesLoaded(true);
		setTemplatesOpen(true);
	}, []);

	// "Start blank" keeps the seeded default design untouched — nothing to
	// mutate; the intro advances into its coach-marks on its own.
	const handleIntroChooseBlank = useCallback(() => {}, []);

	const handleSelectTemplate = useCallback(
		(template: BuilderTemplate) => {
			// A template swaps the whole design in one transaction; re-baseline the
			// milestone latches so it is not mistaken for the user's own progress.
			setMilestoneResyncToken((token) => token + 1);
			builderState.loadTemplate({
				type: template.type,
				dimensions: template.state.dimensions,
				partitionSplit: template.state.partitionSplit,
				openings: template.state.openings,
				deckConfig: template.state.deckConfig,
				roofConfig: template.state.roofConfig,
				thermalInputs: template.state.thermalInputs,
			});
			setTemplatesOpen(false);
		},
		[builderState],
	);

	const saveAndTrack = useCallback(async () => {
		if (await lifecycle.saveNow()) {
			trackConversionEvent("funnel_design_saved", {
				market: builderState.market,
				sauna_type: routing.type,
			});
		}
	}, [builderState.market, lifecycle, routing.type]);

	const handleSaveRequest = useCallback(() => {
		if (routing.type === "tunna") {
			barrelSavePreviousFocusRef.current =
				document.activeElement instanceof HTMLElement
					? document.activeElement
					: null;
			setBarrelSaveConfirmOpen(true);
			return;
		}
		void saveAndTrack();
	}, [routing.type, saveAndTrack]);

	const closeBarrelSaveConfirm = useCallback(() => {
		setBarrelSaveConfirmOpen(false);
		barrelSavePreviousFocusRef.current?.focus();
		barrelSavePreviousFocusRef.current = null;
	}, []);

	const handleConfirmBarrelSave = useCallback(() => {
		closeBarrelSaveConfirm();
		void saveAndTrack();
	}, [closeBarrelSaveConfirm, saveAndTrack]);

	useEffect(() => {
		if (!barrelSaveConfirmOpen) return;

		barrelSaveCancelRef.current?.focus();

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				event.preventDefault();
				closeBarrelSaveConfirm();
				return;
			}

			if (event.key !== "Tab") return;

			const dialog = barrelSaveDialogRef.current;
			if (!dialog) return;
			const focusable = Array.from(
				dialog.querySelectorAll<HTMLElement>(
					'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
				),
			);
			if (focusable.length === 0) return;

			const first = focusable[0];
			const last = focusable[focusable.length - 1];
			if (event.shiftKey && document.activeElement === first) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			}
		};

		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [barrelSaveConfirmOpen, closeBarrelSaveConfirm]);

	if (builderState.loadingDesign) {
		return (
			<div className="builder-layout builder-page builder-page-loading">
				<div className="loading-screen">
					<div className="architectural-loader">
						<svg
							viewBox="0 0 100 100"
							className="wireframe-cube"
							aria-hidden="true"
						>
							<path
								d="M50 15 L90 35 L90 75 L50 95 L10 75 L10 35 Z"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinejoin="round"
							/>
							<path
								d="M50 15 L50 55 M10 35 L50 55 L90 35 M50 55 L50 95"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinejoin="round"
							/>
						</svg>
					</div>
					<span className="loading-text">{t.common_loading}</span>
				</div>
			</div>
		);
	}

	return (
		<BuilderToastProvider>
			<BuilderMilestoneAnnouncer
				rulesCelebrating={milestones.rulesCelebrating}
				// The summary rail is not rendered on the design workspace, so the
				// completeness moment falls back to the toast surface there.
				completenessCelebrating={
					routing.activeWorkspace === "design" &&
					milestones.completenessCelebrating
				}
			/>
			<div
				className={`builder-layout builder-page builder-page--${routing.activeWorkspace}`}
			>
				<BuilderToolbar
					engineLabel={engineLabel}
					projectName={builderState.projectName}
					onProjectNameChange={builderState.setProjectName}
					canUndo={lifecycle.canUndo}
					canRedo={lifecycle.canRedo}
					onUndo={lifecycle.undo}
					onRedo={lifecycle.redo}
					statusText={
						builderState.saveStatus ??
						(routing.designId ? t.badge_autosaved : t.toolbar_draftLocal)
					}
					statusHint={
						builderState.saveStatus
							? undefined
							: routing.designId
								? t.badge_autosavedHint
								: t.toolbar_draftLocalHint
					}
					activeWorkspace={routing.activeWorkspace}
					onExportPdf={actions.handleExportPdf}
					onRenderPhoto={actions.handleRenderPhoto}
					renderLoading={actions.renderLoading}
					onShare={actions.handleShare}
					onSave={handleSaveRequest}
					saving={builderState.saving}
					onOpenTemplates={handleOpenTemplates}
					showTemplates={availableTemplates.length > 0}
					onOpenInsights={() => setInsightsOpen(true)}
				/>

				{routing.type === "tunna" && <DeprecatedBarrelBanner />}

				<BuilderSummaryRail
					mode="mobile"
					estimatedTotal={derived.estimatedTotal}
					projectName={builderState.projectName}
					summaryItems={derived.summaryItems}
					summaryOpen={actions.summaryOpen}
					onSummaryOpenChange={actions.setSummaryOpen}
					activeWorkspaceMeta={routing.activeWorkspaceMeta}
					nextWorkspaceMeta={derived.nextWorkspaceMeta}
					onSetActiveWorkspace={handleWorkspaceAdvance}
					onExportPdf={actions.handleExportPdf}
					completeness={derived.completeness}
				/>

				<BuilderWorkspaceNav
					activeWorkspace={routing.activeWorkspace}
					onSetActiveWorkspace={routing.setActiveWorkspace}
					market={builderState.market}
					badges={derived.workspaceBadges}
					celebrateWorkspace={milestones.rulesCelebrating ? "rules" : null}
				/>

				<div
					id={WORKSPACE_PANEL_ID}
					role="tabpanel"
					aria-labelledby={workspaceTabId(routing.activeWorkspace)}
					className={`builder-content${routing.activeWorkspace === "design" ? " no-rail" : ""}`}
				>
					{routing.activeWorkspace !== "design" && (
						<BuilderSummaryRail
							mode="desktop"
							estimatedTotal={derived.estimatedTotal}
							projectName={builderState.projectName}
							summaryItems={derived.summaryItems}
							summaryOpen={actions.summaryOpen}
							onSummaryOpenChange={actions.setSummaryOpen}
							activeWorkspaceMeta={routing.activeWorkspaceMeta}
							nextWorkspaceMeta={derived.nextWorkspaceMeta}
							onSetActiveWorkspace={handleWorkspaceAdvance}
							onExportPdf={actions.handleExportPdf}
							completeness={derived.completeness}
							completenessCelebrating={milestones.completenessCelebrating}
						/>
					)}

					<WorkspaceRouter
                        cutProjectStorageKey={routing.designId ? `design:${routing.designId}` : `draft:${routing.initialSeedKey}`}
                        onJumpToWorkspace={routing.setActiveWorkspace}
						activeWorkspace={routing.activeWorkspace}
						onReviewSafety={() => handleWorkspaceAdvance("rules")}
						activeWorkspaceMeta={routing.activeWorkspaceMeta}
						designId={routing.designId}
						buildInput={derived.buildInput}
						visibility={builderState.visibility}
						dimensions={builderState.dimensions}
						partitionSplit={builderState.partitionSplit}
						twoRoomConfig={builderState.twoRoomConfig}
						timeOfDay={builderState.timeOfDay}
						rotationDeg={builderState.rotationDeg}
						openings={builderState.openings}
						deckConfig={builderState.deckConfig}
						roofConfig={builderState.roofConfig}
						thermalInputs={builderState.thermalInputs}
						finishes={builderState.finishes}
						finishPriceDeltas={derived.finishPriceDeltas}
						setFinishes={builderState.setFinishes}
						setWallHeightM={builderState.setWallHeightM}
						indoorRoom={builderState.indoorRoom}
						setIndoorRoom={builderState.setIndoorRoom}
						siteConditions={builderState.siteConditions}
						market={builderState.market}
						locationMode={builderState.locationMode}
						climateProfile={builderState.climateProfile}
						postalCode={builderState.postalCode}
						setDimensions={builderState.setDimensions}
						setPartitionSplit={builderState.setPartitionSplit}
						setTwoRoomConfig={builderState.setTwoRoomConfig}
						setTwoRoomPreset={builderState.setTwoRoomPreset}
						setTimeOfDay={builderState.setTimeOfDay}
						setRotationDeg={builderState.setRotationDeg}
						setOpenings={builderState.setOpenings}
						setDeckConfig={builderState.setDeckConfig}
						setRoofConfig={builderState.setRoofConfig}
						setThermalInputs={builderState.setThermalInputs}
						setSiteConditions={builderState.setSiteConditions}
						setClimateProfile={builderState.setClimateProfile}
						setPostalCode={builderState.setPostalCode}
						buildProgress={builderState.buildProgress}
						buildGuide={derived.buildGuide}
						setBuildProgress={builderState.setBuildProgress}
						calcData={derived.calcData}
						heater={derived.heater}
						runningCost={derived.runningCost}
						overrides={overrides}
						engine={routing.engine}
						materialViewMode={actions.materialViewMode}
						onMaterialViewModeChange={actions.setMaterialViewMode}
						isDraggingWindow={actions.isDraggingWindow}
						onStartWindowDrag={actions.handleStartWindowDrag}
						onWindowPlaced={actions.handleWindowPlaced}
						onOpeningMoved={(opening) =>
							builderState.setOpenings((previous) =>
								previous.map((candidate) =>
									candidate.id === opening.id ? opening : candidate,
								),
							)
						}
						onPartitionSplitDragged={builderState.setPartitionSplit}
						onDragCancelled={actions.handleDragCancelled}
						onSnapshotReady={actions.handleSnapshotReady}
						onVisibilityChange={builderState.setVisibility}
						renderError={actions.renderError}
						type={routing.type}
						scaleFigureCapacityHint={builderState.wizardCapacityHint}
						setInteriorLayout={builderState.setInteriorLayout}
					/>

				</div>

				{actions.renderResult && (
					<Suspense fallback={<BuilderSurfaceLoadingFallback />}>
						<BuilderRenderResultModal
							renderResult={actions.renderResult}
							onClose={actions.closeRenderResult}
						/>
					</Suspense>
				)}

				{barrelSaveConfirmOpen && (
					<div className="barrel-save-dialog__backdrop">
						<div
							ref={barrelSaveDialogRef}
							className="barrel-save-dialog"
							role="alertdialog"
							aria-modal="true"
							aria-labelledby="barrel-save-dialog-title"
							aria-describedby="barrel-save-dialog-body"
						>
							<h2 id="barrel-save-dialog-title">
								{t.builder_barrel_saveDeprecatedTitle}
							</h2>
							<p id="barrel-save-dialog-body">
								{t.builder_barrel_saveDeprecatedBody}
							</p>
							<div className="barrel-save-dialog__actions">
								<button
									ref={barrelSaveCancelRef}
									type="button"
									className="btn-action"
									onClick={closeBarrelSaveConfirm}
								>
									{t.common_cancel}
								</button>
								<Link
									className="btn-action"
									to={TWO_ROOM_PANORAMA_MIGRATION_URL}
									onClick={closeBarrelSaveConfirm}
								>
									{t.builder_barrel_saveMigrate}
								</Link>
								<button
									type="button"
									className="btn-action btn-action-accent"
									onClick={handleConfirmBarrelSave}
								>
									{t.builder_barrel_saveContinue}
								</button>
							</div>
						</div>
					</div>
				)}

				<BuilderIntro
					open={introOpen}
					// Barrel has no size presets — without this the entry CTA
					// would open an empty gallery (issue 129).
					hasTemplates={availableTemplates.length > 0}
					onChooseTemplate={handleIntroChooseTemplate}
					onChooseBlank={handleIntroChooseBlank}
					onDismiss={handleIntroDismiss}
				/>

				{actions.shareDialog && (
					<Suspense fallback={<BuilderSurfaceLoadingFallback />}>
						<ShareDialog
							shareUrl={actions.shareDialog.url}
							previewImage={actions.shareDialog.previewImage}
							projectName={actions.shareDialog.projectName}
							dimensions={actions.shareDialog.dimensions}
							estimatedTotal={actions.shareDialog.estimatedTotal}
							heatUpLabel={actions.shareDialog.heatUpLabel}
							onClose={actions.handleCloseShareDialog}
						/>
					</Suspense>
				)}

				{templatesLoaded && (
					<Suspense fallback={<BuilderSurfaceLoadingFallback />}>
						<TemplateGalleryModal
							open={templatesOpen}
							templates={availableTemplates}
							onClose={handleCloseTemplates}
							onSelect={handleSelectTemplate}
						/>
					</Suspense>
				)}

				{insightsOpen && (
					<Suspense fallback={<BuilderSurfaceLoadingFallback />}>
						<DesignInsightsPanel
							open
							onClose={() => setInsightsOpen(false)}
							calcData={derived.calcData}
							heater={derived.heater}
							onJumpToWorkspace={routing.setActiveWorkspace}
						/>
					</Suspense>
				)}

				{routing.activeWorkspace === "design" && (
					<FloatingSpecPill
						estimatedTotal={derived.estimatedTotal}
						floorArea={derived.floorArea}
						calcData={derived.calcData}
						runningCost={derived.runningCost}
						materialViewMode={actions.materialViewMode}
						onMaterialViewModeChange={actions.setMaterialViewMode}
						onJumpToPricing={() => routing.setActiveWorkspace("pricing")}
					/>
				)}

				<MobileActionBar
					activeWorkspace={routing.activeWorkspace}
					activeWorkspaceMeta={routing.activeWorkspaceMeta}
					nextWorkspaceMeta={derived.nextWorkspaceMeta}
					onSetActiveWorkspace={handleWorkspaceAdvance}
				/>
			</div>
		</BuilderToastProvider>
	);
}
